import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'

import { Loader } from '@/components/loaders/Loader'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import {
  deletePlaybook,
  checkPlaybookForUsers,
  reassignUserPlaybooks,
} from '@/reducers/playbooks/playbooks.actions'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { Button } from '@/components/buttons/Button'

export const PlaybookDeleteForm = ({ playbookCid, playbookOptions, onClose }) => {
  const dispatch = useDispatch()
  const [fetched, setFetched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [usersPreventingDelete, setUsersPreventingDelete] = useState([])

  const handleUpdateUserPlaybooks = async (values, { setSubmitting }) => {
    if (!values.playbookCid || isEmpty(usersPreventingDelete)) {
      return
    }

    dispatch(
      reassignUserPlaybooks(
        usersPreventingDelete.map((user) => user.user_id),
        values.playbookCid,
        setSubmitting,
        setUsersPreventingDelete
      )
    )
  }

  const handleDeletePlaybook = async () => {
    const success = await dispatch(deletePlaybook(playbookCid))
    if (success) {
      onClose()
    }
  }

  const getModalContent = () => {
    if (loading || !fetched) {
      return <Loader label="Checking for users..." />
    }

    if (!isEmpty(usersPreventingDelete)) {
      return (
        <div>
          <p>Cannot delete a playbook that is currently assigned to a user.</p>
          <p>Please change the playbooks these users are assigned to and try again.</p>
          <div style={{ marginBottom: '1rem' }}>
            <p className="label">Users</p>
            {usersPreventingDelete.map((user) => (
              <span key={user.user_id} style={{ display: 'block' }}>
                {user.name}
              </span>
            ))}
          </div>
          <AbstractForm
            schema={[
              {
                label: 'Playbook',
                name: 'playbookCid',
                type: 'select',
                options: playbookOptions,
                required: true,
              },
            ]}
            onClose={onClose}
            onSubmit={handleUpdateUserPlaybooks}
            buttonLabel="Apply Changes"
            isModal
          />
        </div>
      )
    }

    return (
      <>
        <p>
          Are you sure you want to delete this playbook? You will lose all versions. This action is
          not reversible.
        </p>
        <div className="modal-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            critical
            data-testid="delete-playbook-button"
            type="button"
            onClick={handleDeletePlaybook}
          >
            Delete
          </Button>
        </div>
      </>
    )
  }

  useEffect(() => {
    if (!playbookCid) {
      return
    }
    dispatch(checkPlaybookForUsers(playbookCid, setUsersPreventingDelete, setLoading, setFetched))
  }, [playbookCid])

  return (
    <BasicModal
      data-testid="delete-playbook-modal"
      title="Delete Playbook"
      onClose={onClose}
      show
      size="small"
    >
      {getModalContent()}
    </BasicModal>
  )
}
